// src/pages/RoutePage.jsx
// Universal reusable component for all 20 cab route SEO pages

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ROUTE_PAGES, fetchRoutePrice } from "../data/routeData";
import axios from "axios";

const C = {
  bg: "#f8f9fd",
  dark: "#1a1a2e",
  gold: "#f5c518",
  goldD: "#d4a80f",
  gray: "#6b7280",
  grayL: "#9ca3af",
  border: "#e5e7eb",
  white: "#ffffff",
};
const F = {
  display: "'Playfair Display', serif",
  body: "'Plus Jakarta Sans', sans-serif",
};

const ROUTE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  html { scroll-behavior:smooth; }
  body { font-family:'Plus Jakarta Sans',sans-serif; overflow-x:hidden; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }
  @keyframes spin   { to{transform:rotate(360deg)} }

  .rp-hero { animation: fadeUp .55s ease both; }
  .rp-card { animation: fadeUp .55s ease both; }
  .rp-card:nth-child(2) { animation-delay:.08s; }
  .rp-card:nth-child(3) { animation-delay:.16s; }

  .rp-book-btn {
    background: linear-gradient(135deg,#f5c518,#d4a80f);
    color: #1a1a2e;
    border: none;
    border-radius: 999px;
    padding: 18px 36px;
    font-size: 17px;
    font-weight: 800;
    cursor: pointer;
    font-family: 'Plus Jakarta Sans', sans-serif;
    box-shadow: 0 14px 40px rgba(245,197,24,.32);
    transition: transform .2s, box-shadow .2s;
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }
  .rp-book-btn:hover { transform:translateY(-2px); box-shadow:0 20px 50px rgba(245,197,24,.42); }

  .rp-call-btn {
    background: #fff;
    color: #1a1a2e;
    border: 1.5px solid #e5e7eb;
    border-radius: 999px;
    padding: 18px 28px;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Plus Jakarta Sans', sans-serif;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    transition: border-color .2s, box-shadow .2s;
  }
  .rp-call-btn:hover { border-color:#f5c518; box-shadow:0 8px 24px rgba(0,0,0,.07); }

  .rp-info-row { display:flex; gap:24px; flex-wrap:wrap; margin-bottom:36px; }
  .rp-info-chip {
    display:flex; align-items:center; gap:8px;
    background:#fff; border:1px solid #e5e7eb; border-radius:999px;
    padding:10px 18px; font-size:14px; font-weight:700; color:#1a1a2e;
    box-shadow:0 4px 12px rgba(0,0,0,.05);
  }

  .rp-highlight {
    display:flex; align-items:center; gap:10px;
    padding:12px 0; border-bottom:1px solid #f0f2f8;
    font-size:14px; font-weight:600; color:#374151;
  }
  .rp-highlight:last-child { border-bottom:none; }

  .rp-landmark {
    display:flex; align-items:center; gap:10px;
    padding:10px 14px; border-radius:12px; background:#f8f9fd;
    border:1px solid #e5e7eb; font-size:13px; font-weight:600; color:#374151;
    margin-bottom:8px;
  }

  .rp-vehicle-card {
    background:#fff; border:1.5px solid #e5e7eb; border-radius:18px;
    padding:20px 14px; text-align:center;
    transition:border-color .2s, box-shadow .2s, transform .2s;
    cursor:pointer;
  }
  .rp-vehicle-card:hover {
    border-color:#f5c518; box-shadow:0 12px 32px rgba(245,197,24,.15); transform:translateY(-4px);
  }

  .rp-faq-item {
    background:#fff; border-radius:14px; border:1.5px solid #e5e7eb;
    overflow:hidden; transition:border-color .25s; cursor:pointer; margin-bottom:12px;
  }
  .rp-faq-item.open { border-color:#f5c518; box-shadow:0 4px 20px rgba(245,197,24,.12); }

  .rp-whatsapp-float {
    position:fixed; bottom:90px; right:18px; width:52px; height:52px;
    border-radius:50%; background:#25D366; display:flex; align-items:center;
    justify-content:center; box-shadow:0 8px 24px rgba(0,0,0,.25); z-index:999;
    cursor:pointer; transition:transform .3s; text-decoration:none; font-size:26px;
  }
  .rp-whatsapp-float:hover { transform:scale(1.1); }

  .rp-breadcrumb { font-size:13px; color:#9ca3af; margin-bottom:20px; }
  .rp-breadcrumb a { color:#f5c518; text-decoration:none; font-weight:600; }
  .rp-breadcrumb a:hover { text-decoration:underline; }

  .rp-loader-overlay {
    position:fixed; inset:0; background:rgba(255,255,255,.75);
    backdrop-filter:blur(4px); z-index:99999;
    display:flex; align-items:center; justify-content:center; flex-direction:column;
  }
  .rp-loader {
    width:55px; height:55px;
    border:5px solid #eee; border-top:5px solid #f5c518;
    border-radius:50%; animation:spin 1s linear infinite;
  }

  @media (max-width:768px) {
    .rp-hero-grid      { grid-template-columns:1fr !important; }
    .rp-info-row       { gap:10px; }
    .rp-info-chip      { font-size:13px; padding:8px 14px; }
    .rp-vehicle-grid   { grid-template-columns:repeat(2,1fr) !important; }
    .rp-content-grid   { grid-template-columns:1fr !important; }
    .rp-hero-padding   { padding:40px 16px 52px !important; }
    .rp-section-pad    { padding:48px 16px !important; }
  }
`;

const VEHICLES = [
  { label: "Hatchback",        seats: "4 Seats",  icon: "🚗", desc: "Economy • City rides"      },
  { label: "Sedan",            seats: "4 Seats",  icon: "🚖", desc: "Comfort • Popular choice"  },
  { label: "SUV",              seats: "7 Seats",  icon: "🚙", desc: "Spacious • Family trips"   },
  { label: "Tempo Traveller",  seats: "14 Seats", icon: "🚌", desc: "Groups • Large families"   },
];

// ── Structured Data ───────────────────────────────────────────────────────────
function RouteStructuredData({ route }) {
  const baseUrl = "https://halosorath.in";
  const pageUrl = `${baseUrl}/${route.slug}`;

  // 1. TaxiService
  const taxiService = {
    "@context": "https://schema.org",
    "@type": "TaxiService",
    "name": route.isAirport
      ? `${route.from} Airport Taxi – Halo Sorath`
      : `${route.from} to ${route.to} Cab – Halo Sorath`,
    "description": route.seoDescription,
    "url": pageUrl,
    "telephone": "+919265407669",
    "priceRange": route.price,
    "currenciesAccepted": "INR",
    "paymentAccepted": "Cash, UPI, Card",
    "areaServed": [
      {
        "@type": "City",
        "name": route.from,
        "addressRegion": route.fromState,
        "addressCountry": "IN",
      },
      {
        "@type": "City",
        "name": route.to,
        "addressRegion": route.toState,
        "addressCountry": "IN",
      },
    ],
    "provider": {
      "@type": "LocalBusiness",
      "name": "Halo Sorath",
      "url": baseUrl,
      "telephone": "+919265407669",
      "email": "info@halosorath.in",
      "image": `${baseUrl}/logo.png`,
      "logo": `${baseUrl}/logo.png`,
      "sameAs": ["https://wa.me/919265407669"],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Junagadh",
        "addressLocality": "Junagadh",
        "addressRegion": "Gujarat",
        "postalCode": "362001",
        "addressCountry": "IN",
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "21.5222",
        "longitude": "70.4579",
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday",
        ],
        "opens": "00:00",
        "closes": "23:59",
      },
     
    },
  };

  // 2. Product / Offer  →  triggers price snippet in Google results
  const product = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": route.isAirport
      ? `${route.from} Airport Cab Service`
      : `${route.from} to ${route.to} Cab`,
    "description": route.description,
    "url": pageUrl,
    "brand": { "@type": "Brand", "name": "Halo Sorath" },
    "offers": {
      "@type": "Offer",
      "url": pageUrl,
      "priceCurrency": "INR",
      "price": route.price.replace(/[^0-9]/g, "") || "5250",
      "priceValidUntil": new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      )
        .toISOString()
        .split("T")[0],
      "availability": "https://schema.org/InStock",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": { "@type": "Organization", "name": "Halo Sorath" },
    },
   
  };

  // 3. BreadcrumbList  →  breadcrumb trail in search results
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",     "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      {
        "@type": "ListItem",
        "position": 3,
        "name": route.isAirport
          ? `${route.from} Airport Taxi`
          : `${route.from} to ${route.to} Cab`,
        "item": pageUrl,
      },
    ],
  };

  // 4. FAQPage  →  expandable FAQ accordion in Google results
  const faqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `How much does a cab from ${route.from} to ${route.to} cost?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `The starting fare for a cab from ${route.from} to ${route.to} is ${route.price}. Final price depends on vehicle type and stops. All rates are transparent with no hidden charges.`,
        },
      },
      {
        "@type": "Question",
        "name": `How long does it take to travel from ${route.from} to ${route.to}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `The journey from ${route.from} to ${route.to} takes approximately ${route.duration} covering about ${route.distance}. Travel time may vary based on traffic.`,
        },
      },
      {
        "@type": "Question",
        "name": `Is the ${route.from} to ${route.to} cab available 24 hours?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Yes, Halo Sorath provides 24×7 cab service from ${route.from} to ${route.to}. You can call or WhatsApp +91 92654 07669 any time to book.`,
        },
      },
      {
        "@type": "Question",
        "name": "Are there any hidden charges?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No hidden charges. The quoted fare includes toll, parking, and all taxes. What you see is what you pay.",
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(taxiService) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(product) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqPage) }} />
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RoutePage() {
  const { slug }    = useParams();
  const navigate    = useNavigate();

  const [openFaq,    setOpenFaq]    = useState(null);
  const [pickup,     setPickup]     = useState("");
  const [dest,       setDest]       = useState("");
  const [travelDate, setTravelDate] = useState(new Date().toISOString().split("T")[0]); // ← controlled date
  const [loading,    setLoading]    = useState(false);
  const [livePrice,  setLivePrice]  = useState(null); // ← live API price

  const pickupRef = useRef(null);
  const destRef   = useRef(null);

  const route = ROUTE_PAGES.find((r) => r.slug === slug);

  // Pre-fill pickup / dest from route data
  useEffect(() => {
    if (!route) return;
    setPickup(route.from);
    setDest(route.to);
  }, [route]);

  // Fetch live price from API
  useEffect(() => {
    if (!route) return;
    fetchRoutePrice(route.from, route.to).then(setLivePrice);
  }, [route]);

  // Google Places autocomplete
  useEffect(() => {
    if (!window.google || !pickupRef.current || !destRef.current) return;
    const pa = new window.google.maps.places.Autocomplete(pickupRef.current, {
      types: ["geocode"],
      componentRestrictions: { country: "in" },
    });
    const da = new window.google.maps.places.Autocomplete(destRef.current, {
      types: ["geocode"],
      componentRestrictions: { country: "in" },
    });
    pa.addListener("place_changed", () => {
      const p = pa.getPlace();
      setPickup(p.formatted_address || "");
    });
    da.addListener("place_changed", () => {
      const p = da.getPlace();
      setDest(p.formatted_address || "");
    });
  }, []);

  // 404 fallback
  if (!route) {
    return (
      <div style={{ fontFamily: F.body, textAlign: "center", padding: "120px 24px" }}>
        <h1 style={{ fontSize: 32, color: C.dark, marginBottom: 16 }}>Route not found</h1>
        <button
          onClick={() => navigate("/")}
          style={{ background: C.gold, border: "none", borderRadius: 999, padding: "14px 32px", fontWeight: 800, fontSize: 15, cursor: "pointer" }}
        >
          Go Home
        </button>
      </div>
    );
  }

  // Display price: live price from API, or fallback to hardcoded
  const displayPrice = livePrice ?? route.price;

  const faqs = [
    {
      q: `How much does a cab from ${route.from} to ${route.to} cost?`,
      a: `The starting fare for a cab from ${route.from} to ${route.to} is ${displayPrice}. Final price depends on the vehicle type (Hatchback, Sedan, SUV) and any additional stops. All rates are transparent with no hidden charges.`,
    },
    {
      q: `How long does it take to travel from ${route.from} to ${route.to}?`,
      a: `The journey from ${route.from} to ${route.to} takes approximately ${route.duration} covering about ${route.distance}. Travel time may vary based on traffic and route conditions.`,
    },
    {
      q: "Can I book a round trip cab?",
      a: `Yes! Halo Sorath offers both one-way and round-trip cab bookings from ${route.from} to ${route.to}. For round trips, you can choose your return date at the time of booking.`,
    },
    {
      q: "Are there any hidden charges?",
      a: "No hidden charges at all. The fare quoted includes toll, parking, and all taxes. We believe in 100% price transparency. What you see is what you pay.",
    },
  ];

  const handleSearch = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://prashantbha.pythonanywhere.com/api/find-cabs/",
        {
          params: {
            pickup:    pickup    || route.from,
            drop:      dest      || route.to,
            trip_type: "one",
            date:      travelDate, // ← uses the controlled state value
          },
        }
      );
      const cabList = res.data.cabs || res.data || [];
      navigate("/find-drivers", {
        state: {
          trip: {
            pickup:      pickup    || route.from,
            destination: dest      || route.to,
            startDate:   travelDate, // ← actual selected date
            endDate:     "",
            tripType:    "one",
          },
          cabsData: cabList,
        },
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Loading overlay */}
      {loading && (
        <div className="rp-loader-overlay">
          <div className="rp-loader" />
          <p style={{ marginTop: 16, fontWeight: 700, color: "#1a1a2e", fontSize: 16 }}>
            Finding Best Cabs...
          </p>
        </div>
      )}

      <Helmet>
        <title>{route.seoTitle}</title>
        <meta name="description" content={route.seoDescription} />
        <meta
          name="keywords"
          content={`${route.from} to ${route.to} cab, ${route.from} ${route.to} taxi, cab booking ${route.from}, taxi ${route.to}, Halo Sorath, Gujarat cab`}
        />
        <link rel="canonical" href={`https://halosorath.in/${route.slug}`} />
        {/* Open Graph */}
        <meta property="og:title"       content={route.seoTitle} />
        <meta property="og:description" content={route.seoDescription} />
        <meta property="og:url"         content={`https://halosorath.in/${route.slug}`} />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="Halo Sorath" />
        {/* Twitter Card */}
        <meta name="twitter:card"        content="summary" />
        <meta name="twitter:title"       content={route.seoTitle} />
        <meta name="twitter:description" content={route.seoDescription} />
      </Helmet>

      <RouteStructuredData route={route} />

      <style>{ROUTE_CSS}</style>

      <Header defaultActive="" />

      {/* ── Hero ── */}
      <div
        className="rp-hero-padding"
        style={{
          background: "linear-gradient(135deg,#fffdf5 0%,#f8f9fd 40%,#fff7d6 100%)",
          padding: "60px 24px 72px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background glows */}
        <div style={{ position: "absolute", top: -100, right: -100, width: 400, height: 400, borderRadius: "50%", background: "rgba(245,197,24,.10)", filter: "blur(40px)", zIndex: 0 }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 280, height: 280, borderRadius: "50%", background: "rgba(26,26,46,.06)", filter: "blur(30px)", zIndex: 0 }} />

        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
          {/* Breadcrumb */}
          <nav className="rp-breadcrumb" aria-label="Breadcrumb">
            <a href="/">Home</a> &nbsp;›&nbsp;
            <a href="/services">Services</a> &nbsp;›&nbsp;
            <span style={{ color: C.dark, fontWeight: 700 }}>
              {route.isAirport ? `${route.from} Airport Taxi` : `${route.from} to ${route.to} Cab`}
            </span>
          </nav>

          <div className="rp-hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 48, alignItems: "center" }}>

            {/* ── Left ── */}
            <div className="rp-hero">
              {/* Badge */}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(245,197,24,.12)", border: "1px solid rgba(245,197,24,.25)", padding: "7px 16px", borderRadius: 999, marginBottom: 20 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.gold, animation: "pulse 1.8s infinite", display: "inline-block" }} />
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: "#b8860b", textTransform: "uppercase" }}>
                  {route.isAirport ? "Airport Transfer Service" : "Outstation Cab Booking"}
                </span>
              </div>

              {/* H1 */}
              <h1 style={{ fontFamily: F.display, fontSize: "clamp(32px,5vw,58px)", fontWeight: 900, color: C.dark, lineHeight: 1.08, marginBottom: 18, letterSpacing: "-1.5px" }}>
                {route.isAirport ? (
                  <>{route.from} <span style={{ color: C.goldD }}>Airport Taxi</span></>
                ) : (
                  <>{route.from} to <span style={{ color: C.goldD }}>{route.to}</span> Cab</>
                )}
              </h1>

              <p style={{ fontSize: "clamp(14px,1.8vw,17px)", color: C.gray, lineHeight: 1.85, maxWidth: 560, marginBottom: 28 }}>
                {route.description}
              </p>

              {/* Info chips — uses live price */}
              <div className="rp-info-row">
                {!route.isAirport && (
                  <>
                    <div className="rp-info-chip">📍 {route.distance}</div>
                    <div className="rp-info-chip">⏱ {route.duration}</div>
                  </>
                )}
                <div className="rp-info-chip" style={{ background: "#fffbea", borderColor: "rgba(245,197,24,.3)", color: C.goldD }}>
                  💰 Starts at {displayPrice}
                </div>
              </div>

              {/* CTAs */}
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                <button className="rp-book-btn" onClick={handleSearch}>
                  🔍 Book This Cab
                </button>
                <a href="tel:+919265407669" className="rp-call-btn">
                  📞 Call Now
                </a>
                <a href="https://wa.me/919265407669" target="_blank" rel="noreferrer" className="rp-call-btn" style={{ borderColor: "#d1fae5", color: "#15803d" }}>
                  💬 WhatsApp
                </a>
              </div>
            </div>

            {/* ── Right — Booking Mini Form ── */}
            <div style={{ background: "rgba(255,255,255,.82)", backdropFilter: "blur(14px)", border: "1px solid rgba(255,255,255,.7)", borderRadius: 28, padding: "28px 24px", boxShadow: "0 20px 60px rgba(0,0,0,.10)" }}>
              <h2 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 800, marginBottom: 6, color: C.dark }}>Quick Booking</h2>
              <p style={{ fontSize: 13, color: C.grayL, marginBottom: 20 }}>Fill details & find best cabs instantly</p>

              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 18 }}>

                {/* Pickup */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.gray, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6, display: "block" }}>Pickup</label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>📍</span>
                    <input
                      ref={pickupRef}
                      value={pickup}
                      onChange={(e) => setPickup(e.target.value)}
                      placeholder="Enter pickup location"
                      style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "14px 14px 14px 40px", fontSize: 14, fontFamily: F.body, outline: "none", color: "#1a1a1a" }}
                    />
                  </div>
                </div>

                {/* Destination */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.gray, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6, display: "block" }}>Destination</label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>🏁</span>
                    <input
                      ref={destRef}
                      value={dest}
                      onChange={(e) => setDest(e.target.value)}
                      placeholder="Enter destination"
                      style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "14px 14px 14px 40px", fontSize: 14, fontFamily: F.body, outline: "none", color: "#1a1a1a" }}
                    />
                  </div>
                </div>

                {/* Date — now controlled */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: C.gray, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6, display: "block" }}>Travel Date</label>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>📅</span>
                    <input
                      type="date"
                      min={new Date().toISOString().split("T")[0]}
                      value={travelDate}
                      onChange={(e) => setTravelDate(e.target.value)}
                      style={{ width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "14px 14px 14px 40px", fontSize: 14, fontFamily: F.body, outline: "none", color: "#1a1a1a" }}
                    />
                  </div>
                </div>
              </div>

              <button
                className="rp-book-btn"
                onClick={handleSearch}
                style={{ width: "100%", justifyContent: "center", fontSize: 16, borderRadius: 14 }}
              >
                Find Best Cabs →
              </button>

              <p style={{ textAlign: "center", marginTop: 12, fontSize: 12, color: C.grayL }}>
                🔒 Safe · Secure · No hidden charges
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Trust bar ── */}
      <div style={{ background: C.dark, padding: "14px 24px", borderTop: `3px solid ${C.gold}`, borderBottom: `3px solid ${C.gold}` }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "8px 32px" }}>
          {["✅ Verified Drivers", "💰 Transparent Pricing", "📞 24×7 Support", "🚗 AC Vehicles"].map((t, i) => (
            <span key={i} style={{ fontSize: 12, fontWeight: 700, color: C.gold, letterSpacing: "0.5px", textTransform: "uppercase" }}>{t}</span>
          ))}
        </div>
      </div>

      {/* ── Main Content ── */}
      <div style={{ background: C.bg }}>
        <div
          className="rp-content-grid"
          style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 340px", gap: 32, padding: "60px 24px" }}
        >

          {/* ── LEFT COLUMN ── */}
          <div>

            {/* About This Route */}
            <section style={{ marginBottom: 48 }}>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, color: C.dark, marginBottom: 18 }}>
                {route.isAirport
                  ? `${route.from} Airport Taxi Service`
                  : `${route.from} to ${route.to} Cab – Everything You Need to Know`}
              </h2>

              <p style={{ fontSize: 15, color: C.gray, lineHeight: 1.85, marginBottom: 16 }}>{route.description}</p>
              <p style={{ fontSize: 15, color: C.gray, lineHeight: 1.85, marginBottom: 16 }}>{route.popularFor}</p>

              {!route.isAirport && (
                <div style={{ background: "#fff", borderRadius: 18, padding: "22px 20px", border: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 20, marginTop: 24 }}>
                  {[
                    { label: "Distance",      val: route.distance,  icon: "📍" },
                    { label: "Duration",       val: route.duration,  icon: "⏱"  },
                    { label: "Starting Fare",  val: displayPrice,    icon: "💰" }, // ← live price
                    { label: "Cab Type",       val: "AC / Non-AC",   icon: "❄️" },
                  ].map((s, i) => (
                    <div key={i} style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 24, marginBottom: 6 }}>{s.icon}</div>
                      <div style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, color: C.dark, marginBottom: 4 }}>{s.val}</div>
                      <div style={{ fontSize: 11, color: C.grayL, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Why Book */}
            <section style={{ marginBottom: 48 }}>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(20px,2.5vw,28px)", fontWeight: 800, color: C.dark, marginBottom: 20 }}>
                Why Book with Halo Sorath?
              </h2>
              <div style={{ background: "#fff", borderRadius: 18, padding: "8px 20px", border: `1px solid ${C.border}` }}>
                {[
                  ...route.highlights,
                  "Experienced & verified drivers",
                  "Clean, well-maintained vehicles",
                  "24×7 customer support",
                ].map((h, i) => (
                  <div key={i} className="rp-highlight">
                    <span style={{ color: C.gold, fontSize: 18 }}>✔</span>
                    {h}
                  </div>
                ))}
              </div>
            </section>

            {/* Vehicle Options */}
            <section style={{ marginBottom: 48 }}>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(20px,2.5vw,28px)", fontWeight: 800, color: C.dark, marginBottom: 20 }}>
                Available Vehicles
              </h2>
              <div className="rp-vehicle-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
                {VEHICLES.map((v, i) => (
                  <div key={i} className="rp-vehicle-card">
                    <div style={{ fontSize: 32, marginBottom: 10 }}>{v.icon}</div>
                    <div style={{ fontFamily: F.display, fontSize: 15, fontWeight: 800, color: C.dark, marginBottom: 4 }}>{v.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.grayL, marginBottom: 6 }}>{v.seats}</div>
                    <div style={{ fontSize: 11, color: C.grayL }}>{v.desc}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Landmarks */}
            <section style={{ marginBottom: 48 }}>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(20px,2.5vw,28px)", fontWeight: 800, color: C.dark, marginBottom: 20 }}>
                Top Places to Visit in {route.isAirport ? route.from : route.to}
              </h2>
              {route.landmarks.map((l, i) => (
                <div key={i} className="rp-landmark">
                  <span style={{ color: C.gold }}>📌</span>
                  {l}
                </div>
              ))}
            </section>

            {/* FAQ */}
            <section>
              <h2 style={{ fontFamily: F.display, fontSize: "clamp(20px,2.5vw,28px)", fontWeight: 800, color: C.dark, marginBottom: 24 }}>
                Frequently Asked Questions
              </h2>
              {faqs.map((f, i) => (
                <div
                  key={i}
                  className={`rp-faq-item ${openFaq === i ? "open" : ""}`}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", gap: 12 }}>
                    <span style={{ fontSize: "clamp(13px,1.5vw,15px)", fontWeight: 700, color: C.dark, lineHeight: 1.4 }}>{f.q}</span>
                    <span style={{ color: C.gold, fontSize: 22, fontWeight: 300, display: "inline-block", transform: openFaq === i ? "rotate(45deg)" : "none", transition: "transform .25s", flexShrink: 0 }}>+</span>
                  </div>
                  {openFaq === i && (
                    <div style={{ padding: "12px 20px 18px", fontSize: 13, color: C.gray, lineHeight: 1.75, borderTop: `1px solid ${C.border}` }}>
                      {f.a}
                    </div>
                  )}
                </div>
              ))}
            </section>
          </div>

          {/* ── RIGHT SIDEBAR ── */}
          <div>
            <div style={{ position: "sticky", top: 90 }}>

              {/* Contact card */}
              <div style={{ background: C.dark, borderRadius: 24, padding: "28px 22px", marginBottom: 20, boxShadow: "0 16px 48px rgba(0,0,0,.15)" }}>
                <h3 style={{ fontFamily: F.display, fontSize: 20, fontWeight: 800, color: C.white, marginBottom: 6 }}>Book Instantly</h3>
                <p style={{ fontSize: 13, color: "rgba(255,255,255,.55)", marginBottom: 20 }}>Call or WhatsApp to confirm your cab</p>

                <a
                  href="tel:+919265407669"
                  style={{ display: "flex", alignItems: "center", gap: 12, background: C.gold, borderRadius: 14, padding: "16px 18px", textDecoration: "none", marginBottom: 12, transition: "opacity .2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = ".85")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <span style={{ fontSize: 22 }}>📞</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: C.dark }}>+91 92654 07669</div>
                    <div style={{ fontSize: 11, color: "rgba(26,26,46,.7)", fontWeight: 600 }}>Tap to call now</div>
                  </div>
                </a>

                <a
                  href="https://wa.me/919265407669"
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "flex", alignItems: "center", gap: 12, background: "#25D366", borderRadius: 14, padding: "16px 18px", textDecoration: "none", transition: "opacity .2s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = ".85")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <span style={{ fontSize: 22 }}>💬</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15, color: C.white }}>WhatsApp Us</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 600 }}>Quick reply guaranteed</div>
                  </div>
                </a>

                {/* Live price badge */}
                <div style={{ marginTop: 20, padding: "14px 16px", background: "rgba(245,197,24,.08)", borderRadius: 12, border: "1px solid rgba(245,197,24,.2)" }}>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Starting fare</div>
                  <div style={{ fontFamily: F.display, fontSize: 32, fontWeight: 900, color: C.gold }}>
                    {displayPrice} {/* ← live price */}
                  </div>
                  {!route.isAirport && (
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)", marginTop: 4 }}>
                      for {route.distance} • {route.duration}
                    </div>
                  )}
                </div>
              </div>

              {/* Other popular routes */}
              <div style={{ background: "#fff", borderRadius: 20, padding: "20px 18px", border: `1px solid ${C.border}` }}>
                <h4 style={{ fontFamily: F.display, fontSize: 16, fontWeight: 800, color: C.dark, marginBottom: 16 }}>
                  Other Popular Routes
                </h4>
                {ROUTE_PAGES
                  .filter((r) => r.slug !== route.slug && (r.from === route.from || r.to === route.to))
                  .slice(0, 5)
                  .map((r, i) => (
                    <a
                      key={i}
                      href={`/${r.slug}`}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f3f4f6", textDecoration: "none" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = C.goldD; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = ""; }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>
                        {r.isAirport ? `${r.from} Airport Taxi` : `${r.from} → ${r.to}`}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.goldD }}>{r.price}</span>
                    </a>
                  ))}
                <a href="/" style={{ display: "block", marginTop: 14, fontSize: 13, fontWeight: 700, color: C.goldD, textDecoration: "none", textAlign: "center" }}>
                  View All Routes →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Floating WhatsApp */}
      <a href="https://wa.me/919265407669" target="_blank" rel="noreferrer" className="rp-whatsapp-float" aria-label="Chat on WhatsApp">
        💬
      </a>
    </>
  );
}